import { useEffect, useRef } from "react";
import { useSettingsSafe } from "@shared/contexts/SettingsContext";

const MIN_INTERVAL_SECONDS = 30;
const MAX_INTERVAL_SECONDS = 300;

function clampAutoSaveIntervalSeconds(value: number | undefined): number {
  if (!Number.isFinite(value) || typeof value !== "number") {
    return 60;
  }
  return Math.max(MIN_INTERVAL_SECONDS, Math.min(MAX_INTERVAL_SECONDS, value));
}

/**
 * Persists dirty editors on the Application Settings auto-save interval.
 * Skip until `auto_save` is on and `isDirty` is true.
 */
export function useAutoSave(
  isDirty: boolean,
  onSave: () => void | Promise<void>,
  options?: { enabled?: boolean },
): void {
  const { settings } = useSettingsSafe();
  const enabled = options?.enabled ?? Boolean(settings.auto_save);
  const intervalSeconds = clampAutoSaveIntervalSeconds(
    settings.auto_save_interval,
  );
  const onSaveRef = useRef(onSave);
  const savingRef = useRef(false);

  onSaveRef.current = onSave;

  useEffect(() => {
    if (!enabled || !isDirty) return undefined;

    const timerId = window.setInterval(() => {
      if (savingRef.current) return;
      savingRef.current = true;
      void Promise.resolve(onSaveRef.current()).finally(() => {
        savingRef.current = false;
      });
    }, intervalSeconds * 1000);

    return () => window.clearInterval(timerId);
  }, [enabled, intervalSeconds, isDirty]);
}
